import { Box, Button, Card, CardContent, CardHeader } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import type { Permission, RoleDefinition } from "@xop/dashboard-shared";
import { useEffect, useMemo, useState, type JSX } from "react";

import { ApiError } from "../../../lib/api.js";
import { PermissionGrid } from "./PermissionGrid.js";
import { roleSubheader } from "./roleText.js";
import { RoleStatusChips } from "./RoleStatusChips.js";

/**
 * One role's card — collapsed by default (header + status chips stay visible
 * so the list scans), expanding to the editable permission grid + Save / Reset.
 * Owns the local `draft` permission set, re-synced whenever the server's
 * effective set changes. `super_admin` (protected) renders read-only with no
 * action buttons. Extracted from `RolesPanel.tsx`.
 */
export function RoleCard({
  role,
  canEdit,
  isSaving,
  saveError,
  onSave,
  onResetRequest,
}: {
  role: RoleDefinition;
  canEdit: boolean;
  isSaving: boolean;
  saveError: unknown;
  onSave: (permissions: Permission[]) => void;
  onResetRequest: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Set<Permission>>(
    () => new Set(role.effective_permissions),
  );

  // Roles are collapsed by default — the matrix is dense and reviewing it for
  // ten roles at once is overwhelming. Admins expand only the role they're
  // inspecting / editing. Header + badges stay visible when collapsed.
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyId = `role-${role.role}-perms`;

  // Re-sync whenever the server's effective set changes (e.g. after a
  // successful save, or another admin edits the same role in another tab).
  // Without this, the local draft would silently shadow the remote update and
  // the next save would clobber it.
  useEffect(() => {
    setDraft(new Set(role.effective_permissions));
  }, [role.effective_permissions]);

  const dirty = useMemo(() => {
    if (draft.size !== role.effective_permissions.length) return true;
    return role.effective_permissions.some((p) => !draft.has(p));
  }, [draft, role.effective_permissions]);

  const isProtected = role.protected;
  const disabled = isProtected || !canEdit;

  function toggle(perm: Permission, checked: boolean): void {
    if (disabled) return;
    setDraft((prev) => {
      const next = new Set(prev);
      if (checked) next.add(perm);
      else next.delete(perm);
      return next;
    });
  }

  const apiError = saveError instanceof ApiError ? saveError : null;

  const title = (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${role.label} permissions`}
        sx={{
          ml: -0.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 1,
          border: 0,
          bgcolor: "transparent",
          p: 0.5,
          cursor: "pointer",
          font: "inherit",
          color: "inherit",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&:focus-visible": {
            outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.6)}`,
            outlineOffset: 1,
          },
        }}
      >
        {isExpanded ? (
          <ExpandMoreIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        )}
        <Box component="span">{role.label}</Box>
      </Box>
      <Box
        component="code"
        sx={{
          borderRadius: 0.5,
          bgcolor: "surfaceSoft",
          px: 0.75,
          py: 0.25,
          fontFamily: "ui-monospace, monospace",
          fontSize: 10,
          color: "text.disabled",
        }}
      >
        {role.role}
      </Box>
      <RoleStatusChips role={role} isExpanded={isExpanded} dirty={dirty} />
    </Box>
  );

  const action =
    isExpanded && canEdit && !isProtected ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {role.customized && (
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={onResetRequest}
            aria-label={`Reset ${role.label} to baseline`}
            startIcon={<RotateLeftIcon sx={{ fontSize: 14 }} />}
          >
            Reset
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={!dirty || isSaving}
          onClick={() => onSave([...draft])}
          aria-label={`Save permissions for ${role.label}`}
        >
          {isSaving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </Button>
      </Box>
    ) : null;

  return (
    <Card data-slot="card">
      <CardHeader
        title={title}
        subheader={roleSubheader(role)}
        action={action}
        sx={{
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          py: 2,
          "& .MuiCardHeader-content": { minWidth: 0 },
          "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 600, color: "text.primary" },
          "& .MuiCardHeader-subheader": { fontSize: 12, color: "text.disabled" },
          "& .MuiCardHeader-action": { m: 0, alignSelf: "center" },
        }}
      />
      {isExpanded && (
        <CardContent sx={{ px: 2.5, py: 2 }}>
          <Box id={bodyId}>
            {apiError && (
              <Box sx={{ mb: 1.5 }}>
                <Box
                  role="alert"
                  sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: (t) => alpha(t.palette.error.main, 0.4),
                    bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                    px: 1.5,
                    py: 1,
                    fontSize: 12,
                    color: "error.main",
                  }}
                >
                  {apiError.message}
                </Box>
              </Box>
            )}
            <PermissionGrid
              role={role.role}
              draft={draft}
              disabled={disabled}
              onToggle={toggle}
            />
          </Box>
        </CardContent>
      )}
    </Card>
  );
}
